import { Box, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { customDashboardAnalytics } from '../../customDashboardAnalytics'
import { customDashboardsHref } from '../../routing/routes'

// We use container queries here because we're in a variable width section on the dashboard (sidebar can be collapsed).
// Below this width the body wraps past two lines, so the banner drops the design min-height and
// the artwork. The height is a minimum, not fixed, so longer copy (translations, text zoom) grows
// the banner instead of being clipped by the Paper's overflow: hidden.
const NARROW_LAYOUT_BREAKPOINT = 800
const BANNER_MIN_HEIGHT = 144
const ARTWORK_COLUMN_WIDTH = 260

// The design hangs a 257px-square artwork over a 238px-wide window, overflowing 19px right and
// vertically centred (to within 2px), bleeding off the banner's top and bottom. Sizes are
// percentages of the column width so the composition holds regardless of banner height, and the
// image is absolutely positioned so it never stretches the banner; the Paper clips the bleed.
const ARTWORK_WIDTH = '108.2%'
const ARTWORK_RIGHT = '-8.2%'

export const CustomDashboardsBanner = () => {
  const { t } = useTranslation('reporting', { keyPrefix: 'custom-dashboards.home-banner' })
  const { user } = useAuthenticatedUser()

  if (!user.isAdmin) {
    return null
  }

  return (
    <Paper sx={{ maxWidth: '100%', mb: 3, containerType: 'inline-size', overflow: 'hidden' }}>
      <Stack
        direction="row"
        sx={(theme) => ({
          minHeight: BANNER_MIN_HEIGHT,
          [theme.containerQueries.down(NARROW_LAYOUT_BREAKPOINT)]: {
            minHeight: 'auto',
          },
        })}
      >
        <Stack sx={{ flexGrow: 1, px: 5, py: 4, justifyContent: 'center' }}>
          <Typography variant="upsized-semiBold">{t('share-title')}</Typography>
          <Typography variant="body-regular" sx={{ color: 'text.muted' }}>
            {t('share-body')}
          </Typography>

          <Stack direction="row" gap={3} sx={{ pt: 2 }}>
            <InternalLinkButton
              variant="contained"
              color="primary"
              href={customDashboardsHref}
              onClick={() => customDashboardAnalytics.homeBannerClicked()}
            >
              {t('share-cta')}
            </InternalLinkButton>
          </Stack>
        </Stack>

        <Box
          sx={(theme) => ({
            width: ARTWORK_COLUMN_WIDTH,
            flexShrink: 0,
            position: 'relative',
            [theme.containerQueries.down(NARROW_LAYOUT_BREAKPOINT)]: {
              display: 'none',
            },
          })}
        >
          <Box
            component="img"
            src="/app/assets/custom-dashboards-banner.png"
            alt=""
            sx={{
              position: 'absolute',
              width: ARTWORK_WIDTH,
              right: ARTWORK_RIGHT,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
        </Box>
      </Stack>
    </Paper>
  )
}
